import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { legal, legalPage } from '@/data/content';
import Button from '@/components/ui/Button';
import { SpinnerIcon } from '@/components/ui/Icons';

interface LegalPageProps {
    slug?: string;
}

const LegalPage: React.FC<LegalPageProps> = ({ slug: slugOverride = '' }) => {
    const { slug: slugFromRoute } = useParams<{ slug: string }>();
    const slug = slugOverride || slugFromRoute;
    const [data, setData] = useState<{ title: string; content: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const loadContent = () => {
            setLoading(true);
            setError('');

            if (slug && legal[slug]) {
                setData(legal[slug]);
            } else {
                setError(legalPage.content_unavailable);
            }
            setLoading(false);
        };

        if (slug) {
            loadContent();
        } else {
            setLoading(false);
            setError(legalPage.invalid_page);
        }
    }, [slug]);

    return (
        <section className="py-10 md:py-14 bg-white dark:bg-gray-950 min-h-screen">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <Button
                    variant="secondary"
                    size="sm"
                    className="mb-6"
                    onClick={() => navigate('/')}
                    icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            ></path>
                        </svg>
                    }
                >
                    {legalPage.back_button}
                </Button>

                {loading && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-10 border border-gray-200 dark:border-gray-800 flex flex-col items-center justify-center min-h-[40vh]">
                        <SpinnerIcon className="w-12 h-12 text-app-primary mb-4" />
                        <span className="text-gray-500 font-medium">{legalPage.loading}</span>
                    </div>
                )}

                {error && !loading && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-10 border border-gray-200 dark:border-gray-800 flex flex-col items-center justify-center min-h-[40vh]">
                        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                ></path>
                            </svg>
                        </div>
                        <p className="text-red-500 font-medium text-lg">{error}</p>
                    </div>
                )}

                {!loading && !error && data && (
                    <article className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
                        <div className="px-6 py-8 md:px-10 md:py-10 border-b border-gray-200 dark:border-gray-800">
                            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white m-0">
                                {data.title}
                            </h1>
                        </div>
                        <div className="px-6 py-8 md:px-10 md:py-10">
                            <div
                                className="prose max-w-none dark:prose-invert prose-p:text-[15px] prose-p:leading-7 prose-headings:font-semibold prose-headings:text-gray-900 prose-h1:text-3xl prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2 prose-h3:text-base prose-h3:mt-5 prose-h3:mb-2 prose-strong:font-semibold dark:prose-headings:text-white prose-a:text-app-primary hover:prose-a:text-orange-600 prose-img:rounded-2xl prose-img:shadow-sm"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content || '') }}
                            />
                        </div>
                    </article>
                )}
            </div>
        </section>
    );
};

export default LegalPage;
